// Shared client types for the DFS lineup query contract (all 3 sports). Server contract: Api/CLAUDE.md
// "DFS lineup query contract". Property names are lowercase and match the API responses exactly.

export type LineupSport = 'baseball' | 'basketball' | 'football';
export type LineupSortField = 'totalEnhancedScore' | 'totalProjected' | 'totalSalary';
export type LineupSortDir = 'asc' | 'desc';

export interface LineupFilterState {
  includeIds: number[];
  excludeIds: number[];
  /** List-only: never reaches auto-select, multi-entry, export, byIds or Save. */
  search: string;
  projMin: number | null;
  projMax: number | null;
  salaryMin: number | null;
  salaryMax: number | null;
  sort: LineupSortField;
  dir: LineupSortDir;
}

export const DEFAULT_LINEUP_FILTER: LineupFilterState = {
  includeIds: [],
  excludeIds: [],
  search: '',
  projMin: null,
  projMax: null,
  salaryMin: null,
  salaryMax: null,
  sort: 'totalEnhancedScore',
  dir: 'desc',
};

/** One entry per player; `ids` holds ALL of that player's DK rows on the slate (CPT/UTIL twins). */
export interface PlayerOption {
  key: string;
  name: string;
  teamAbbrev: string;
  position: string;
  ids: number[];
}

export interface BandStats { min: number; p33: number; p67: number; max: number }
export interface LineupBands {
  count: number;
  /** null when the slate has no lineups. */
  projected: BandStats | null;
  salary: BandStats | null;
}

export interface LineupPage<T> {
  lineups: T[];
  totalCount: number;
  /** Server page number (1-based). */
  page: number;
  pageSize: number;
}

/**
 * Most ids one `POST .../lineups/byIds` accepts: the client mirror of the server's `LineupQueryLimits.MaxByIds` (the server
 * 400s above it). `apiService.lineupQuery.getByIds` splits a larger selection into requests of this size.
 */
export const LINEUP_BYIDS_MAX = 5000;

export interface LineupsByIds<T> {
  lineups: T[];
  /** Ids no longer stored (a rebuild removed them). */
  missingIds: number[];
  /** Ids still stored but removed by the selection filter that was sent with the request. */
  filteredOutIds: number[];
}

/** Carried by auto-select / multi-entry responses; `restored` is football only. */
export interface SelectionMeta { requested: number; returned: number; restored?: number }

/** An auto-select / multi-entry response: the RETURNED rows (Save/Export use them as-is, never re-fetched by id) + meta. */
export interface SelectionResponse<T> extends SelectionMeta { lineups: T[] }

/**
 * Returns a human-readable problem (or null) for a filter the server would reject with 400 (min > max).
 * Pages should skip the request while this is non-null.
 */
export function filterProblem(f: LineupFilterState): string | null {
  if (f.projMin != null && f.projMax != null && f.projMin > f.projMax) return 'Projection min is above max.';
  if (f.salaryMin != null && f.salaryMax != null && f.salaryMin > f.salaryMax) return 'Salary min is above max.';
  return null;
}

/**
 * Identity of the SELECTION part of a filter (include, exclude, both ranges; never search or sort). Two filters with the same
 * key select the same lineups, so an auto set built under one key is stale once the current filter's key differs.
 */
export function selectionFilterKey(f: LineupFilterState): string {
  const ids = (xs: number[]) => [...xs].sort((a, b) => a - b).join(',');
  return [ids(f.includeIds), ids(f.excludeIds), f.projMin, f.projMax, f.salaryMin, f.salaryMax].map((v) => v ?? '').join('|');
}
